import type { FindOperationsInput } from '@travel-planner/shared/schemas/operations';
import type { MessagesTabListFilter } from './cards';

// Which FindOperationsInput keys are worth surfacing in a card's info tooltip
// (XT-341 / XT-380): the fields that actually narrow the query, not
// pagination/sort/search/scope noise shared by every card.
const QUERY_KEYS = [
  'startDate',
  'endDate',
  'tourOperatorAssigned',
  'flagged',
  'failedMessages',
  'operationStatuses',
  'operationStatusCodes',
  'operationStatusCodesExclude',
  'contentCategory',
  'contentSubcategory',
  'venue',
  'priorityVenuesOnly',
  'hasAttachments',
  'withoutPdf',
  'minTravellers',
  'minDurationHours',
  'languageCodeExclude',
  'messageStatuses',
  'itineraryDateMode',
  'itineraryStatuses',
  'itineraryStatusTransitionedTo',
  'itineraryCancelledOnWithin',
  'includeNonOperational',
  'skipDateRangeFilter',
  'countMode',
] as const;

// Renders the resolved operations.list filter as a compact, monospace-ready
// string so the tooltip shows exactly what clicking the card will query —
// not just a prose description of it.
export const formatCardQuery = (
  input: Partial<FindOperationsInput>
): string => {
  const parts: string[] = [];
  for (const key of QUERY_KEYS) {
    const value = input[key];
    if (value === undefined || value === false) continue;
    if (key === 'startDate' || key === 'endDate') {
      parts.push(`${key}=${String(value).slice(0, 10)}`);
      continue;
    }
    if (Array.isArray(value)) {
      if (value.length <= 3 && value.every((v) => typeof v === 'string')) {
        parts.push(`${key}=${value.join(',')}`);
      } else {
        parts.push(
          `${key}=${value.length} val${value.length === 1 ? '' : 's'}`
        );
      }
      continue;
    }
    parts.push(`${key}=${value}`);
  }
  return parts.join('  ');
};

/** Compact tooltip for Messages-tab cards (external-messages list filters). */
export const formatMessagesCardQuery = (
  filter: MessagesTabListFilter
): string => {
  const parts: string[] = [];
  if (filter.status) parts.push(`status=${filter.status}`);
  if (filter.maxChars) parts.push('maxChars=true');
  if (filter.operationStatusCodes?.length) {
    parts.push(`operationStatusCodes=${filter.operationStatusCodes.join(',')}`);
  }
  return parts.join('  ');
};
